import React, { memo } from 'react';
import { useQuery } from '@apollo/client';
import { QUERY_USER_HOME } from '../graphql/query/user/queryUserHome';
import {
  isAllowedToAccesDashboard,
  MY_ID,
  needToCompleteProfile,
  ROLE,
  USER_ROLE
} from '../helpers/user/authorization';
import { ErrorContainer } from 'components/organisms/Error/ErrorContainer';
import { MESSAGES } from '../helpers/messages';
import logout from '../graphql/logout';
import FrontError from '../helpers/errors/front.error';
import NotAuthorizedEleve from '../components/layouts/NotAuthorizedEleve';
import { LoadingLayout } from 'components/molecules/Loaders/LoadingLayout';
import SignIn from '../components/organisms/forms/SignIn';
import { ContainerFullWidth } from 'components/layouts/ContainerFullWidth';
import { FeatureContainer } from 'components/molecules/Containers/FeatureContainer';
import { setUser } from '../store/actions/user.actions';
import { useDispatch, useSelector } from 'react-redux';
import ListMenuSidebar from '../components/layouts/LoggedLayout/Menu/MenuSidebar';
import LoggedLayout from '../components/layouts/LoggedLayout';
import { LoginLayout } from 'components/layouts/LoginLayout';
import routes from '../routes/routes';
import { Redirect, Route, Switch } from 'react-router';
import pages from '../routes/pages';
import { GetUserOnHomeQuery } from '../graphql/generated/graphql';
import { ClasseChoice } from '../components/organisms/ClasseChoice';
import { selectClasseChoice } from 'store/selectors/ui.selectors';

const LoggedViews = memo(() => {
  const classChoiceStatus = useSelector(selectClasseChoice);
  const dispatch = useDispatch();
  const { data, loading, error } = useQuery<GetUserOnHomeQuery>(
    QUERY_USER_HOME,
    {
      onCompleted: userData =>
        dispatch(
          setUser({
            ...userData.getUserOnHome,
            role: userData.getUserOnHome.role as ROLE,
            classe: {
              ...userData.getUserOnHome.classe,
              lycee: {
                name: userData.getUserOnHome?.classe?.lycee
              }
            }
          })
        )
    }
  );

  if (loading) {
    return <LoadingLayout variant="centeredOnly" />;
  }

  if (error) {
    if (error.message === 'GraphQL error: Forbidden' && data?.getUserOnHome)
      return (
        <LoginLayout>
          <>
            <ContainerFullWidth>
              <FeatureContainer>
                <ErrorContainer />
              </FeatureContainer>
            </ContainerFullWidth>
          </>
        </LoginLayout>
      );

    return (
      <LoginLayout>
        <>
          <ContainerFullWidth>
            <FeatureContainer>
              <ErrorContainer
                text={MESSAGES.error.session}
                onClick={logout}
                detailErr={
                  new FrontError(
                    "L'utilisateur est incorrect ou session expirée " +
                      error.message,
                    {
                      error,
                      idUserForGraphQL: MY_ID
                    }
                  )
                }
                needCaptureError={false}
              />
            </FeatureContainer>
          </ContainerFullWidth>
        </>
      </LoginLayout>
    );
  }

  const user = {
    ...data.getUserOnHome,
    role: data.getUserOnHome.role as ROLE,
    classe: {
      ...data.getUserOnHome.classe,
      lycee: {
        name: data.getUserOnHome.classe?.lycee
      }
    }
  };

  if (data) {
    if (needToCompleteProfile(user)) return <NotAuthorizedEleve />;
    if (
      USER_ROLE === 'professeur' &&
      (!user.classe.lycee.name || classChoiceStatus)
    )
      return <ClasseChoice />;
    if (isAllowedToAccesDashboard(!!user?.classe?.operationid))
      return (
        <LoggedLayout menu={<ListMenuSidebar />}>
          <Switch>
            {routes.map((route, i) => (
              <Route
                key={i}
                exact
                path={route.path}
                component={route.component}
              />
            ))}
            <Redirect to={pages.HOME_PAGE.path} />
          </Switch>
        </LoggedLayout>
      );
  }
  return <SignIn />;
});

export default LoggedViews;

LoggedViews.displayName = 'LoggedViews';
